import * as React from "react";
import { useCallback, useEffect } from "react";
import type { ToasterProps } from "sonner";
import { Toaster, toast } from "sonner";
import { useTheme } from "@/components/admin/theme-provider";
import {
  CloseNotificationContext,
  useNotificationContext,
  useTakeUndoableMutation,
  useTranslate,
} from "ra-core";

const localizeNotificationMessage = (message: string) => {
  const normalized = message.trim().toLowerCase();

  if (!normalized) {
    return message;
  }

  if (normalized.includes("invalid login credentials")) {
    return "E-mail ou senha inválidos.";
  }

  if (normalized.includes("new password should be different from the old password")) {
    return "A nova senha deve ser diferente da senha atual";
  }

  if (normalized.includes("email not confirmed")) {
    return "E-mail não confirmado.";
  }

  if (normalized.includes("password should be at least")) {
    return "A senha deve ter no mínimo 6 caracteres.";
  }

  if (
    normalized.includes("jwt expired") ||
    normalized.includes("token has expired") ||
    normalized.includes("session expired")
  ) {
    return "Sessão expirada. Faça login novamente.";
  }

  if (
    normalized.includes("permission denied") ||
    normalized.includes("not authorized") ||
    normalized.includes("forbidden")
  ) {
    return "Sem permissão para esta ação.";
  }

  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("network request failed") ||
    normalized.includes("network error")
  ) {
    return "Falha de conexão. Verifique a internet.";
  }

  if (normalized === "http error") {
    return "Erro HTTP";
  }

  if (normalized === "an error occurred") {
    return "Ocorreu um erro";
  }

  if (normalized.includes("user not found")) {
    return "Usuário não encontrado.";
  }

  return message;
};

export const Notification = (props: ToasterProps) => {
  const translate = useTranslate();
  const { notifications, takeNotification } = useNotificationContext();
  const takeMutation = useTakeUndoableMutation();
  const { theme } = useTheme();

  useEffect(() => {
    if (notifications.length) {
      const notification = takeNotification();
      if (notification) {
        const { message, type = "info", notificationOptions } = notification;
        const { messageArgs, undoable } = notificationOptions || {};

        const beforeunload = (e: BeforeUnloadEvent) => {
          e.preventDefault();
          const confirmationMessage = "";
          e.returnValue = confirmationMessage;
          return confirmationMessage;
        };

        if (undoable) {
          window.addEventListener("beforeunload", beforeunload);
        }

        const handleExited = () => {
          if (undoable) {
            const mutation = takeMutation();
            if (mutation) {
              mutation({ isUndo: false });
            }
            window.removeEventListener("beforeunload", beforeunload);
          }
        };

        const handleUndo = () => {
          const mutation = takeMutation();
          if (mutation) {
            mutation({ isUndo: true });
          }
          window.removeEventListener("beforeunload", beforeunload);
        };

        const finalMessage = message
          ? typeof message === "string"
            ? localizeNotificationMessage(translate(message, messageArgs))
            : React.isValidElement(message)
              ? message
              : undefined
          : undefined;

        toast[type](finalMessage, {
          action: undoable
            ? {
                label: translate("ra.action.undo"),
                onClick: handleUndo,
              }
            : undefined,
          onDismiss: handleExited,
          onAutoClose: handleExited,
        });
      }
    }
  }, [notifications, takeMutation, takeNotification, translate]);

  const handleRequestClose = useCallback(() => {
    // Dismiss all toasts
    toast.dismiss();
  }, []);

  return (
    <CloseNotificationContext.Provider value={handleRequestClose}>
      <Toaster
        richColors
        theme={theme}
        closeButton
        position="bottom-center"
        {...props}
      />
    </CloseNotificationContext.Provider>
  );
};
